import { Route, Navigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { resilientLazy } from "@/lib/resilientImport";

const Index = resilientLazy(() => import("@/pages/Index"), "Index");
const DescobrirExperiencias = resilientLazy(() => import("@/pages/DescobrirExperiencias"), "DescobrirExperiencias");
// ExperienciaDetalhe is legacy — /experiencia/:slug now redirects to /aula/:slug
const AulaDetalhe = resilientLazy(() => import("@/pages/AulaDetalhe"), "AulaDetalhe");
const MateriaDetalhe = resilientLazy(() => import("@/pages/MateriaDetalhe"), "MateriaDetalhe");
const MinhaEstante = resilientLazy(() => import("@/pages/MinhaEstante"), "MinhaEstante");
const ExperienciasParaVoce = resilientLazy(() => import("@/pages/ExperienciasParaVoce"), "ExperienciasParaVoce");
const TeacherProfile = resilientLazy(() => import("@/pages/TeacherProfile"), "TeacherProfile");
const Dashboard = resilientLazy(() => import("@/pages/Dashboard"), "Dashboard");
const Profile = resilientLazy(() => import("@/pages/Profile"), "Profile");
const HumanizedProfile = resilientLazy(() => import("@/pages/HumanizedProfile"), "HumanizedProfile");
const ComoFunciona = resilientLazy(() => import("@/pages/ComoFunciona"), "ComoFunciona");
const ParaEducadores = resilientLazy(() => import("@/pages/ParaEducadores"), "ParaEducadores");
const EnsineNaAuli = resilientLazy(() => import("@/pages/EnsineNaAuli"), "EnsineNaAuli");
const Ajuda = resilientLazy(() => import("@/pages/Ajuda"), "Ajuda");
const TermosDeUso = resilientLazy(() => import("@/pages/TermosDeUso"), "TermosDeUso");
const PoliticaPrivacidade = resilientLazy(() => import("@/pages/PoliticaPrivacidade"), "PoliticaPrivacidade");
const Onboarding = resilientLazy(() => import("@/pages/Onboarding"), "Onboarding");
const CentralEncontros = resilientLazy(() => import("@/pages/CentralEncontros"), "CentralEncontros");
const Notificacoes = resilientLazy(() => import("@/pages/Notificacoes"), "Notificacoes");
const ApplyToBeTeacher = resilientLazy(() => import("@/pages/ApplyToBeTeacher"), "ApplyToBeTeacher");
const NotFound = resilientLazy(() => import("@/pages/NotFound"), "NotFound");
const SeoLandingPage = resilientLazy(() => import("@/pages/SeoLandingPage"), "SeoLandingPage");
const CertificadoPublico = resilientLazy(() => import("@/pages/CertificadoPublico"), "CertificadoPublico");
const NossosEducadores = resilientLazy(() => import("@/pages/NossosEducadores"), "NossosEducadores");
const JoinMeeting = resilientLazy(() => import("@/pages/JoinMeeting"), "JoinMeeting");

// Legacy redirect component for old teacher profile URLs
const LegacyTeacherRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/professor/${id}`} replace />;
};

// Legacy redirect: /educador/:slugOrId → /professor/:slugOrId
const LegacyEducadorRedirect = () => {
  const { slugOrId } = useParams();
  return <Navigate to={`/professor/${slugOrId}`} replace />;
};

// Legacy redirect: /experiencia/:slug → /aula/:slug
const LegacyExperienciaRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/aula/${slug}`} replace />;
};

export const PublicRoutes = () => (
  <>
    {/* Home */}
    <Route path={ROUTES.HOME} element={<Index />} />
    
    {/* Discovery and Exploration */}
    <Route path={ROUTES.DISCOVER_EXPERIENCES} element={<DescobrirExperiencias />} />
    <Route path={ROUTES.EXPERIENCE_DETAIL} element={<LegacyExperienciaRedirect />} />
    <Route path={ROUTES.CLASS_DETAIL} element={<AulaDetalhe />} />
    <Route path={ROUTES.SUBJECT_DETAIL} element={<MateriaDetalhe />} />
    <Route path={ROUTES.MY_SHELF} element={<MinhaEstante />} />
    <Route path={ROUTES.EXPERIENCES_FOR_YOU} element={<ExperienciasParaVoce />} />
    
    {/* Teacher Directory */}
    <Route path={ROUTES.FIND_TEACHERS} element={<NossosEducadores />} />
    
    {/* Profile Pages */}
    <Route path={ROUTES.TEACHER_PROFILE} element={<TeacherProfile />} />
    <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
    <Route path={ROUTES.ONBOARDING} element={<Onboarding />} />
    <Route path={ROUTES.PROFILE} element={<Profile />} />
    <Route path={ROUTES.HUMANIZED_PROFILE} element={<HumanizedProfile />} />
    
    {/* Footer and Info Pages */}
    <Route path={ROUTES.HOW_IT_WORKS} element={<ComoFunciona />} />
    <Route path={ROUTES.MANIFESTO_TEACHERS} element={<EnsineNaAuli />} />
    <Route path={ROUTES.APPLY_TO_TEACH} element={<ApplyToBeTeacher />} />
    <Route path={ROUTES.HELP} element={<Ajuda />} />
    <Route path={ROUTES.CENTRAL_ENCONTROS} element={<CentralEncontros />} />
    <Route path={ROUTES.NOTIFICATIONS} element={<Notificacoes />} />
    
    {/* Redirects */}
    <Route path={ROUTES.FOR_EDUCATORS} element={<Navigate to={ROUTES.MANIFESTO_TEACHERS} replace />} />
    <Route path="/ensine-na-auli" element={<Navigate to={ROUTES.MANIFESTO_TEACHERS} replace />} />
    <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermosDeUso />} />
    <Route path={ROUTES.PRIVACY_POLICY} element={<PoliticaPrivacidade />} />
    <Route path="/politica-privacidade" element={<Navigate to={ROUTES.PRIVACY_POLICY} replace />} />
    
    {/* Instant Meetings */}
    <Route path={ROUTES.INSTANT_MEETING} element={<JoinMeeting />} />
    
    {/* Certificates */}
    <Route path="/certificado/:shareToken" element={<CertificadoPublico />} />
    
    {/* Redirects from old routes */}
    <Route path={ROUTES.EXPLORE_SUBJECTS} element={<Navigate to={ROUTES.DISCOVER_EXPERIENCES} replace />} />
    <Route path={ROUTES.BROWSE} element={<Navigate to={ROUTES.DISCOVER_EXPERIENCES} replace />} />
    <Route path={ROUTES.BROWSE_CLASSES} element={<Navigate to={ROUTES.DISCOVER_EXPERIENCES} replace />} />
    <Route path="/materias/:slug" element={<Navigate to="/descobrir-experiencias" replace />} />
    <Route path={ROUTES.FAVORITE_SUBJECTS} element={<Navigate to={ROUTES.MY_SHELF} replace />} />
    <Route path={ROUTES.EXPLORED_SUBJECTS} element={<Navigate to={ROUTES.MY_SHELF} replace />} />
    <Route path={ROUTES.RECOMMENDED_SUBJECTS} element={<Navigate to={ROUTES.EXPERIENCES_FOR_YOU} replace />} />
    <Route path="/buscar-experiencias" element={<Navigate to={ROUTES.DISCOVER_EXPERIENCES} replace />} />
    <Route path="/buscar-materias" element={<Navigate to={ROUTES.DISCOVER_EXPERIENCES} replace />} />
    <Route path="/minhas-exploracoes" element={<Navigate to={ROUTES.MY_SHELF} replace />} />
    <Route path={ROUTES.JOURNEY_ADMIN} element={<Navigate to={ROUTES.ADMIN} replace />} />
    
    {/* Legacy redirect: /perfil-professor/:id → /professor/:id */}
    <Route path="/perfil-professor/:id" element={<LegacyTeacherRedirect />} />
    
    {/* SEO redirect: /educador/:slugOrId → /professor/:slugOrId (Cloudflare intercepts for bots) */}
    <Route path="/educador/:slugOrId" element={<LegacyEducadorRedirect />} />
    
    {/* Deprecated routes */}
    <Route path={ROUTES.DEPRECATED_TEACHER_SIGNUP} element={<Navigate to={ROUTES.HOME} replace />} />
    <Route path={ROUTES.DEPRECATED_FAMILY_SIGNUP} element={<Navigate to={ROUTES.HOME} replace />} />
    
    {/* SEO Programmatic Landing Pages (catch-all, before 404) */}
    <Route path={ROUTES.SEO_LANDING} element={<SeoLandingPage />} />

    {/* 404 - Must be last */}
    <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
  </>
);
